import { ChangeDetectionStrategy, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { Event, NavigationEnd, Router, RouterModule } from '@angular/router';

import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith, withLatestFrom } from 'rxjs/operators';

import { CommonModule } from '@angular/common';

import { ShClickOutsideDirective } from '../../directives/click-outside/click-outside.directive';
import { NavigationLinkDirective } from '../../directives/navigation-link/navigation-link-directive.module';
import { ShBadgeComponent } from '../badge/badge.component';
import { IconSize } from '../icon/entities/icon-sizes.entity';
import { ShIconModule } from '../icon/icon.module';
import { NavigationItem } from './entities/navigation-item.entity';

export * from './entities/navigation-item.entity';
export * from './entities/navigation-link.entity';

@Component({
    standalone: true,
    selector: 'sh-navigation-item',
    templateUrl: 'navigation-item.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ShIconModule,
        RouterModule,
        ShBadgeComponent,
        NavigationLinkDirective,
        ShClickOutsideDirective
    ]
})
export class ShNavigationItemComponent implements OnInit {
    readonly navigationIconSize: IconSize = IconSize.REGULAR;
    readonly dropMenuIconSize: IconSize = IconSize.SMALL;

    private currentLocation$!: Observable<string>;
    isActive$!: Observable<boolean>;
    activeSubActionItemLabel$!: Observable<string>;

    @Input() item!: NavigationItem;
    @Input() openDropmenuAbove = false;

    @ViewChild('detailsElement', { read: ElementRef }) detailsElement!: ElementRef<HTMLDetailsElement>;

    constructor(private readonly router: Router) {}

    get hasDropmenuItems(): boolean {
        return 'dropmenuItems' in this.item;
    }

    closeSubMenu(): void {
        this.detailsElement.nativeElement.removeAttribute('open');
    }

    ngOnInit(): void {
        this.initStateStreams();
    }

    private initStateStreams(): void {
        this.currentLocation$ = this.router.events.pipe(
            filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd),
            map((event: NavigationEnd) => event.urlAfterRedirects),
            startWith(this.router.url),
            distinctUntilChanged(),
            shareReplay()
        );

        this.isActive$ = this.currentLocation$.pipe(
            map((url: string) =>
                this.item.navigationLink.route ? url.includes(this.item.navigationLink.route) : false
            ),
            shareReplay()
        );

        this.activeSubActionItemLabel$ = this.currentLocation$.pipe(
            withLatestFrom(this.isActive$),
            filter(([_, isActive]) => isActive),
            map(([currentLocation, _]) => this.getActiveSubActionItemLabel(currentLocation))
        );
    }

    private getActiveSubActionItemLabel(currentLocation: string): string {
        let activeSubItem: NavigationItem;
        const allSubActionItems: NavigationItem[] | undefined = this.item.dropmenuItems;

        if (allSubActionItems && allSubActionItems.length) {
            activeSubItem = allSubActionItems.filter(
                (item: NavigationItem) => currentLocation === item.navigationLink.route
            )[0];
            return activeSubItem ? activeSubItem.label : '';
        } else {
            return '';
        }
    }
}
